import { useLocation, useNavigate } from 'react-router-dom';
import { useMemo } from 'react';

export interface FilterAdapter<T> {
  key: string; // query key, e.g. "entity_type"
  fromUrl: (sp: URLSearchParams) => T | null;
  toUrl: (sp: URLSearchParams, value: T | null) => URLSearchParams;
  // optional to help cache keys / equality checks
  toKey?: (value: T | null) => string;
}

export type AnyFilterValue = any;

export class UrlFilterController {
  constructor(
    private pathname: string,
    private search: string,
    private navigate: ReturnType<typeof useNavigate>
  ) {}

  get<T>(adapter: FilterAdapter<T>): T | null {
    const sp = new URLSearchParams(this.search);
    return adapter.fromUrl(sp);
  }

  set<T>(adapter: FilterAdapter<T>, value: T | null, replace = true) {
    const current = new URLSearchParams(this.search);
    const next = adapter.toUrl(current, value);
    this.search = next.toString();
    const nextUrl = `${this.pathname}?${next.toString()}`;
    this.navigate(nextUrl, { replace });
  }

  buildSearchParams(
    adaptersWithValues: Array<{ adapter: FilterAdapter<any>; value: AnyFilterValue }>,
    base?: URLSearchParams
  ): URLSearchParams {
    let sp = new URLSearchParams(base ? base.toString() : '');
    for (const { adapter, value } of adaptersWithValues) {
      const normalized = Array.isArray(value) && value.length === 0 ? null : (value as any);
      sp = adapter.toUrl(sp, normalized ?? null);
    }
    return sp;
  }

  buildCacheKey(adapters: FilterAdapter<any>[]): string {
    const sp = new URLSearchParams(this.search);
    const parts = adapters.map((a) =>
      a.toKey ? a.toKey(a.fromUrl(sp)) : `${a.key}:${sp.get(a.key) ?? '∅'}`
    );
    return parts.join('|');
  }

  resetToDefault(adapters: FilterAdapter<any>[], replace = true) {
    let sp = new URLSearchParams();
    for (const adapter of adapters) {
      sp = adapter.toUrl(sp, null);
    }
    const url = `${this.pathname}?${sp.toString()}`;
    this.navigate(url, { replace });
  }
}

export function useUrlFilterController() {
  const location = useLocation();
  const navigate = useNavigate();
  return useMemo(
    () => new UrlFilterController(location.pathname, location.search, navigate),
    [location.pathname, location.search, navigate]
  );
}
